import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './CreatePostModal.module.css';
import HTMLEditor from '../ShipturtleProducts/HTMLEditor';
import { richTextToPlain } from '../../utils/richText';
import { attachmentDisplayName } from '../../utils/attachments';

const PLAN_OPTIONS = ['Seed', 'Root', 'Bloom', 'Thrive'];

/** Escape plain text so an existing TEXT post can be loaded into the HTML editor. */
function textToHtml(text) {
  const escaped = String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .split(/\n{2,}/)
    .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

function newRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for older browsers / insecure origins where randomUUID is absent.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB

function UploadCloudIcon() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.4 14.5a4.5 4.5 0 0 0-2.6-8.1 6 6 0 0 0-11.4 1.6A4.2 4.2 0 0 0 5 16.3" />
      <polyline points="9 13 12 10 15 13" />
      <line x1="12" y1="10" x2="12" y2="19" />
    </svg>
  );
}

function getDefaultForm() {
  return {
    title: '',
    body: '',
    post_type: 'announcement',
    attachment: null,
    cta_url: '',
    cta_label: '',
    plan_access: 'all',
    plan_selection: [],
    send_email: false,
  };
}

function formFromPost(post) {
  return {
    title: post.title || '',
    // The editor works in HTML. A legacy plain-text post has to be converted on
    // the way in, or its line breaks would collapse.
    body: post.body_format === 'HTML' ? (post.body || '') : textToHtml(post.body),
    post_type: post.post_type || 'announcement',
    attachment: null,
    cta_url: post.cta_url || '',
    cta_label: post.cta_label || '',
    plan_access: post.visibility_gated ? 'selected' : 'all',
    plan_selection: post.gated_plan_name ? [post.gated_plan_name] : [],
    send_email: false,
  };
}

export default function CreatePostModal({ isOpen, onClose, onSubmit, isAdmin, postTypes, editPost }) {
  const [form, setForm] = useState(getDefaultForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const attachRef = useRef(null);
  // Stable for the whole compose session. Retrying after a failed publish sends
  // the same key, so the server returns the post the first attempt created
  // instead of publishing a second copy.
  //
  // Seeded at first render rather than in the open-effect: effects run after
  // render, so a submit that beat the effect would go out with no key at all
  // and lose duplicate protection — exactly the case this exists to cover.
  const requestIdRef = useRef(null);
  if (requestIdRef.current === null) requestIdRef.current = newRequestId();
  // Lets Cancel abort a publish that is still in flight.
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const wasOpenRef = useRef(isOpen);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (isOpen && editPost) {
      setForm(formFromPost(editPost));
    } else if (isOpen) {
      setForm(getDefaultForm());
    }
    // Only a genuine closed -> open transition starts a new compose session.
    // Regenerating unconditionally would replace the key seeded at first render
    // and break the stability a retry depends on.
    if (isOpen && !wasOpenRef.current) requestIdRef.current = newRequestId();
    wasOpenRef.current = isOpen;
    setErrors({});
    setSubmitting(false);
    setIsDragging(false);
    setUploadError('');
  }, [isOpen, editPost]);

  // Abort any in-flight request if the modal goes away.
  useEffect(() => () => abortRef.current?.abort(), []);

  const requestClose = useCallback(() => {
    if (submitting) {
      const proceed = window.confirm(
        'This post is still being published. Cancel it?\n\n' +
          'If the server already saved it, the post will appear in the feed.'
      );
      if (!proceed) return;
      abortRef.current?.abort();
    }
    onClose();
  }, [submitting, onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [requestClose]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const setBody = useCallback((html) => {
    setForm(prev => (prev.body === html ? prev : { ...prev, body: html }));
  }, []);

  /* Shared by the file picker and the drop zone, so a dragged file gets the same
     checks the backend enforces (png/jpg/pdf, 10 MB) instead of failing on submit. */
  const acceptFile = (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setUploadError('Only JPG, PNG or PDF files are allowed.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError('File is larger than 10MB.');
      return;
    }
    setUploadError('');
    set('attachment', file);
  };

  const togglePlan = (plan) => {
    setForm(prev => {
      const sel = prev.plan_selection;
      return {
        ...prev,
        plan_selection: sel.includes(plan) ? sel.filter(p => p !== plan) : [...sel, plan],
      };
    });
  };

  const validate = () => {
    const errs = {};
    if (!form.title.trim()) errs.title = 'Title is required';
    // The editor emits markup even when it looks empty ("<p><br></p>"), so
    // check the rendered text rather than the raw HTML.
    if (!richTextToPlain(form.body)) errs.body = 'Content is required';
    if (form.cta_url && !form.cta_label.trim()) {
      errs.cta_label = 'CTA label is required when a URL is provided';
    }
    if (form.cta_label && !form.cta_url.trim()) {
      errs.cta_url = 'CTA URL is required when a label is provided';
    }
    if (isAdmin && form.plan_access === 'selected' && form.plan_selection.length === 0) {
      errs.plan_selection = 'Select at least one plan or switch to All Users';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    const controller = new AbortController();
    abortRef.current = controller;
    setSubmitting(true);
    try {
      await onSubmit({
        title:             form.title.trim(),
        body:              form.body.trim(),
        body_format:       'HTML',
        post_type:         form.post_type,
        attachment:        form.attachment || null,
        cta_url:           form.cta_url.trim() || null,
        cta_label:         form.cta_label.trim() || null,
        plan_access:       form.plan_access,
        plan_selection:    form.plan_selection,
        send_email:        form.send_email,
        client_request_id: requestIdRef.current,
        signal:            controller.signal,
      });
    } finally {
      abortRef.current = null;
      // The parent unmounts this modal on success; guard so a late settle
      // doesn't set state on an unmounted component.
      if (mountedRef.current) setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const isEditing = !!editPost;
  let modalTitle = isEditing ? 'Edit Post' : 'Submit a Post';
  let submitLabel = submitting
    ? (isEditing ? 'Saving…' : isAdmin ? 'Publishing…' : 'Submitting…')
    : isEditing ? 'Save Changes'
    : isAdmin ? 'Publish Now' : 'Submit for Approval';

  return (
    <div className={styles.overlay} onClick={requestClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>

        {/* ── Modal header ── */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{modalTitle}</h2>
          <button className={styles.closeBtn} onClick={requestClose} aria-label="Close">✕</button>
        </div>

        {/* Brands only — their posts go through review. Admins publish directly,
            so the notice would not be true for them. */}
        {!isAdmin && (
          <p className={styles.formNotice}>
            <span className={styles.formNoticeIcon} aria-hidden="true">💡</span>
            Your post will be reviewed by the Suspire team before it appears in the feed.
          </p>
        )}

        <form className={styles.form} onSubmit={handleSubmit} noValidate>

          {/* ── Post type ── */}
          <div className={styles.field}>
            <label className={styles.label}>Post Type</label>
            <div className={styles.typeGrid}>
              {Object.entries(postTypes).map(([key, info]) => (
                <button
                  key={key}
                  type="button"
                  /* The selected chip is always Suspire teal — the per-type colours
                     (blue/purple/amber) are for the feed category badge, not this. */
                  className={`${styles.typeChip} ${form.post_type === key ? styles.typeChipActive : ''}`}
                  onClick={() => set('post_type', key)}
                >
                  {info.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Title ── */}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="circle-title">
              Title <span className={styles.req}>*</span>
            </label>
            <input
              id="circle-title"
              type="text"
              className={`${styles.input} ${errors.title ? styles.inputError : ''}`}
              placeholder="Give your post a clear, descriptive title"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              maxLength={200}
            />
            <div className={styles.inputMeta}>
              {errors.title && <span className={styles.error}>{errors.title}</span>}
              <span className={styles.charCount}>{form.title.length}/200</span>
            </div>
          </div>

          {/* ── Body ── */}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="circle-body">
              Content <span className={styles.req}>*</span>
            </label>
            <div
              id="circle-body"
              className={`${styles.richEditor} ${errors.body ? styles.richEditorError : ''}`}
            >
              <HTMLEditor
                value={form.body}
                onChange={setBody}
                placeholder="Share your insight, announcement, or story…"
                disabled={submitting}
              />
            </div>
            <span className={styles.editorHint}>
              Formatting from pasted text (headings, bold, lists, links) is preserved.
            </span>
            {errors.body && <span className={styles.error}>{errors.body}</span>}
          </div>

          {/* ── Attachment (image or PDF) ── */}
          <div className={styles.field}>
            <label className={styles.label}>Attachment (optional)</label>
            {isEditing && editPost.attachment_url && !form.attachment && (
              <p className={styles.existingAttachment}>
                Current: {attachmentDisplayName(editPost, 'file')}
              </p>
            )}
            <div
              className={`${styles.uploadBox} ${isDragging ? styles.uploadBoxDragging : ''}`}
              onClick={() => attachRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                acceptFile(e.dataTransfer.files?.[0]);
              }}
            >
              {form.attachment ? (
                <span className={styles.uploadSelected}>
                  {form.attachment.type.startsWith('image/') ? '🖼️' : '📄'} {form.attachment.name}
                </span>
              ) : (
                <>
                  <span className={styles.uploadIcon}><UploadCloudIcon /></span>
                  <span className={styles.uploadPlaceholder}>
                    {isEditing && editPost.attachment_url ? 'Select a file to replace' : 'Select a file'}
                  </span>
                  <span className={styles.uploadHint}>
                    JPG, PNG or PDF, file size no more than 10MB
                  </span>
                </>
              )}
              <input
                ref={attachRef}
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                style={{ display: 'none' }}
                onChange={e => acceptFile(e.target.files[0])}
              />
            </div>
            {uploadError && <span className={styles.error}>{uploadError}</span>}
            {form.attachment && (
              <button type="button" className={styles.clearFile} onClick={() => { set('attachment', null); setUploadError(''); }}>
                Remove
              </button>
            )}
          </div>

          {/* ── CTA (label + URL pair) ── */}
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="circle-cta-label">CTA Label</label>
              <input
                id="circle-cta-label"
                type="text"
                className={`${styles.input} ${errors.cta_label ? styles.inputError : ''}`}
                placeholder="e.g. Learn More"
                value={form.cta_label}
                onChange={e => set('cta_label', e.target.value)}
                maxLength={120}
              />
              {errors.cta_label && <span className={styles.error}>{errors.cta_label}</span>}
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="circle-cta-url">CTA URL</label>
              <input
                id="circle-cta-url"
                type="url"
                className={`${styles.input} ${errors.cta_url ? styles.inputError : ''}`}
                placeholder="https://example.com"
                value={form.cta_url}
                onChange={e => set('cta_url', e.target.value)}
              />
              {errors.cta_url && <span className={styles.error}>{errors.cta_url}</span>}
            </div>
          </div>

          {/* ── Admin-only: Plan Access ── */}
          {isAdmin && (
            <>
              <hr className={styles.divider} />
              <div className={styles.field}>
                <label className={styles.label}>Plan Access</label>
                <div className={styles.radioGroup}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="plan_access"
                      value="all"
                      checked={form.plan_access === 'all'}
                      onChange={() => set('plan_access', 'all')}
                    />
                    All Users
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="plan_access"
                      value="selected"
                      checked={form.plan_access === 'selected'}
                      onChange={() => set('plan_access', 'selected')}
                    />
                    Selected Plans Only
                  </label>
                </div>

                {form.plan_access === 'selected' && (
                  <div className={styles.planCheckboxes}>
                    <p className={styles.planCheckboxHint}>
                      Post will only be visible to users on the selected plans:
                    </p>
                    {PLAN_OPTIONS.map(plan => (
                      <label key={plan} className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={form.plan_selection.includes(plan)}
                          onChange={() => togglePlan(plan)}
                        />
                        {plan}
                      </label>
                    ))}
                  </div>
                )}
                {errors.plan_selection && (
                  <span className={styles.error}>{errors.plan_selection}</span>
                )}
              </div>
            </>
          )}

          {/* ── Admin-only: Email Notification ── */}
          {isAdmin && (
            <div className={styles.field}>
              <label className={styles.checkboxLabelInline}>
                <input
                  type="checkbox"
                  checked={form.send_email}
                  onChange={e => set('send_email', e.target.checked)}
                />
                <span>
                  Send email notification to all eligible brands when this post is published
                </span>
              </label>
            </div>
          )}

          {/* ── Actions ── */}
          <div className={styles.formActions}>
            <button type="button" className={styles.cancelBtn} onClick={requestClose}>
              Cancel
            </button>
            <button type="submit" className={styles.submitBtn} disabled={submitting}>
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
